import { Injectable } from '@angular/core';

// The consultant's own saved Adjust instructions, called by name with a slash.
//
// These exist because the presets we would have written are the wrong ones: the
// instructions that actually change a safety record name things in *that* record
// ("mention the 145 mm gap"), which no chip we ship can express. So the product
// ships a way to save instructions, not a guess at what they should say.

export interface AiWritingSkill {
  /** Slug typed after the slash. Unique, lowercase, [a-z0-9-]. */
  id: string;
  /** The text that reaches the model. May carry both do and don't rules. */
  instruction: string;
  /** Only 'personal' today. Present from the start so team-shared skills can be
   *  added without migrating everything already in storage. */
  scope: 'personal';
  createdAt: number;
}

const STORE_KEY = 'ai-writing.styles.v1';
const LEGACY_KEYS = ['ai-writing.skills.v2', 'ai-writing.skills.v1'];

// Seeded on first run only, and deletable like any other. Professional presets
// tailored for functional safety and machinery risk assessment consultants.
const SEEDS: Array<Pick<AiWritingSkill, 'id' | 'instruction'>> = [
  {
    id: 'shorter',
    instruction:
      'Condense wording concisely while strictly preserving all findings, measurements, and technical observations.',
  },
  {
    id: 'detailed',
    instruction:
      'Expand with comprehensive technical detail, elaborating on machine components, operational states, and specific safeguards while adhering strictly to observed facts.',
  },
  {
    id: 'plain',
    instruction:
      'Rephrase for executive clarity while maintaining technical precision and standard machinery safety terminology.',
  },
];

/** Longest instruction the transform route will accept. Mirrors
 *  MAX_INSTRUCTION_LENGTH in app/api/ai-writing/transform/route.ts. */
export const MAX_INSTRUCTION_LENGTH = 2000;

@Injectable({ providedIn: 'root' })
export class AiWritingSkillsService {
  private cache: AiWritingSkill[] | null = null;

  all(): AiWritingSkill[] {
    if (this.cache) return this.cache;
    this.cache = this.read();
    return this.cache;
  }

  /** Skills whose id, or any word of whose instruction, starts with `query`.
   *  Prefix-matching, not substring: a bare "/h" matching "s-h-orter" makes the
   *  menu useless after one keystroke. An empty query returns everything. */
  match(query: string): AiWritingSkill[] {
    const q = (query || '').trim().toLowerCase();
    if (!q) return this.all();
    return this.all().filter(
      (s) =>
        s.id.startsWith(q) ||
        s.instruction.toLowerCase().split(/\s+/).some((w) => w.startsWith(q)),
    );
  }

  get(id: string): AiWritingSkill | undefined {
    return this.all().find((s) => s.id === id);
  }

  /** Turns free text into a usable slug. Returns '' when nothing survives, which
   *  the caller treats as an invalid name rather than saving a blank id. */
  toId(name: string): string {
    return (name || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 32);
  }

  /** Newest first, so a freshly saved skill is the first thing `/` offers. */
  save(id: string, instruction: string): AiWritingSkill | null {
    const slug = this.toId(id);
    const text = (instruction || '').trim().slice(0, MAX_INSTRUCTION_LENGTH);
    if (!slug || !text) return null;
    const skill: AiWritingSkill = { id: slug, instruction: text, scope: 'personal', createdAt: Date.now() };
    this.write([skill, ...this.all().filter((s) => s.id !== slug)]);
    return skill;
  }

  /** Edit in place, including a rename. Unlike save() this keeps the skill where
   *  it already sits in the list: an edit that silently reshuffled the menu would
   *  move every other row too, which is exactly the kind of drift `/` exists to
   *  avoid. Returns null if the new name or text is empty, or if the rename would
   *  collide with a different existing skill. */
  update(oldId: string, newName: string, instruction: string): AiWritingSkill | null {
    const slug = this.toId(newName);
    const text = (instruction || '').trim().slice(0, MAX_INSTRUCTION_LENGTH);
    if (!slug || !text) return null;
    if (slug !== oldId && this.all().some((s) => s.id === slug)) return null;

    const list = this.all();
    const at = list.findIndex((s) => s.id === oldId);
    if (at === -1) return this.save(slug, text);

    const updated: AiWritingSkill = { ...list[at], id: slug, instruction: text };
    const next = [...list];
    next[at] = updated;
    this.write(next);
    return updated;
  }

  remove(id: string): void {
    this.write(this.all().filter((s) => s.id !== id));
  }

  // ——— storage ———
  // Every read and write is guarded: private mode, blocked storage and a key
  // someone else wrote junk into all surface here, and none of them are worth
  // breaking the Adjust bar over. Losing skills degrades to "no skills yet".
  private read(): AiWritingSkill[] {
    let stored: unknown = null;
    try {
      const raw = localStorage.getItem(STORE_KEY);
      stored = raw ? JSON.parse(raw) : null;
    } catch {
      stored = null;
    }
    if (Array.isArray(stored) && stored.length) {
      return stored.filter(
        (s): s is AiWritingSkill =>
          !!s && typeof s.id === 'string' && typeof s.instruction === 'string',
      );
    }

    // Preserve any user-created custom styles from legacy keys (excluding deprecated seeds)
    const customFromLegacy: AiWritingSkill[] = [];
    for (const legacyKey of LEGACY_KEYS) {
      try {
        const legacyRaw = localStorage.getItem(legacyKey);
        const legacyList = legacyRaw ? JSON.parse(legacyRaw) : null;
        if (Array.isArray(legacyList)) {
          for (const item of legacyList) {
            if (
              item &&
              item.id &&
              item.id !== 'harm' &&
              !SEEDS.some((s) => s.id === item.id) &&
              !customFromLegacy.some((c) => c.id === item.id)
            ) {
              customFromLegacy.push(item);
            }
          }
        }
      } catch {
        // ignore
      }
    }

    // First run on styles v1 — seed with professional safety-oriented guidelines
    const seeded: AiWritingSkill[] = [
      ...customFromLegacy,
      ...SEEDS.map((s) => ({ ...s, scope: 'personal' as const, createdAt: Date.now() })),
    ];
    this.write(seeded);
    return seeded;
  }

  private write(list: AiWritingSkill[]): void {
    this.cache = list;
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify(list));
    } catch {
      // Kept in memory for this session; it just won't survive a reload.
    }
  }
}
